import ApiService from './apiService';
import SnagTrackerService from './snagTrackerService';

/**
 * Reports Service
 * Handles analytics and reporting
 */
export interface ReportData {
  totalSnags: number;
  resolvedSnags?: number;
  pendingSnags?: number;
  criticalSnags?: number;
  resolvedCount?: number;
  pendingCount?: number;
  criticalCount?: number;
  byPriority: Record<string, number>;
  byStatus: Record<string, number>;
  byAssignee: Record<string, number>;
  timelineData: Array<{ date: string; count: number }>;
}

export interface SiteReport {
  siteId: string;
  siteName: string;
  projectId: string;
  projectName: string;
  totalSnags: number;
  resolvedCount: number;
  completionRate: number;
  criticalIssues: number;
  averageResolutionTime: number;
}

export const ReportsService = {
  /**
   * Get dashboard overview/KPIs
   */
  getDashboardOverview: async (filters?: {
    startDate?: string;
    endDate?: string;
    projectId?: string;
    siteId?: string;
  }) => {
    try {
      return await ApiService.get<ReportData>('/reports/overview', {
        params: filters
      });
    } catch {
      const response = await SnagTrackerService.getSnags();
      const snags: any[] = response.snags || [];

      const byPriority = snags.reduce((acc: Record<string, number>, snag: any) => {
        acc[snag.priority] = (acc[snag.priority] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const byStatus = snags.reduce((acc: Record<string, number>, snag: any) => {
        acc[snag.status] = (acc[snag.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const byAssignee = snags.reduce((acc: Record<string, number>, snag: any) => {
        const assignee = snag.assignedTo?.name || 'Unassigned';
        acc[assignee] = (acc[assignee] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const timelineData = snags.reduce((acc: Array<{ date: string; count: number }>, snag: any) => {
        const date = new Date(snag.createdDate).toLocaleDateString();
        const existing = acc.find((item: { date: string; count: number }) => item.date === date);
        if (existing) {
          existing.count += 1;
        } else {
          acc.push({ date, count: 1 });
        }
        return acc;
      }, [] as Array<{ date: string; count: number }>).slice(-10);

      const resolvedCount = snags.filter((snag: any) => snag.status === 'Resolved' || snag.status === 'Closed').length;
      const criticalCount = snags.filter((snag: any) => snag.priority === 'Critical').length;
      const pendingCount = snags.length - resolvedCount;

      return {
        totalSnags: snags.length,
        resolvedSnags: resolvedCount,
        pendingSnags: pendingCount,
        criticalSnags: criticalCount,
        resolvedCount,
        pendingCount,
        criticalCount,
        byPriority,
        byStatus,
        byAssignee,
        timelineData,
      };
    }
  },

  /**
   * Get snag distribution by priority
   */
  getSnagsByPriority: async (filters?: any) => {
    return ApiService.get('/reports/snags-by-priority', { params: filters });
  },

  /**
   * Get snag distribution by status
   */
  getSnagsByStatus: async (filters?: any) => {
    return ApiService.get('/reports/snags-by-status', { params: filters });
  },

  /**
   * Get snags assigned by user
   */
  getSnagsByAssignee: async (filters?: any) => {
    return ApiService.get('/reports/snags-by-assignee', { params: filters });
  },

  /**
   * Get snag timeline data
   */
  getSnagTimeline: async (filters?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }) => {
    return ApiService.get('/reports/snag-timeline', { params: filters });
  },

  /**
   * Get site/project reports
   */
  getSiteReports: async (filters?: {
    startDate?: string;
    endDate?: string;
    siteId?: string;
    projectId?: string;
  }) => {
    return ApiService.get<{ reports: SiteReport[] }>('/reports/sites', {
      params: filters
    });
  },

  /**
   * Get project performance report
   */
  getProjectReport: async (projectId: string) => {
    return ApiService.get(`/reports/projects/${projectId}`);
  },

  /**
   * Get team performance report
   */
  getTeamPerformance: async (filters?: any) => {
    return ApiService.get('/reports/team-performance', { params: filters });
  },

  /**
   * Get user activity report
   */
  getUserActivity: async (userId: string) => {
    return ApiService.get(`/reports/users/${userId}/activity`);
  },

  /**
   * Export report as PDF
   */
  exportReportPDF: async (reportType: string, filters?: any) => {
    try {
      return await ApiService.get(`/reports/${reportType}/export/pdf`, {
        params: filters,
        responseType: 'blob'
      });
    } catch {
      const overview = await ReportsService.getDashboardOverview(filters);
      const fallbackContent = [
        `ARQITE ${reportType.toUpperCase()} REPORT`,
        '',
        `Total snags: ${overview.totalSnags || 0}`,
        `Critical snags: ${overview.criticalCount || overview.criticalSnags || 0}`,
        `Resolved snags: ${overview.resolvedCount || overview.resolvedSnags || 0}`,
        `Pending snags: ${overview.pendingCount || overview.pendingSnags || 0}`,
      ].join('\n');

      return new Blob([fallbackContent], { type: 'application/pdf' });
    }
  },

  /**
   * Export report as Excel
   */
  exportReportExcel: async (reportType: string, filters?: any) => {
    try {
      return await ApiService.get(`/reports/${reportType}/export/excel`, {
        params: filters,
        responseType: 'blob'
      });
    } catch {
      const overview = await ReportsService.getDashboardOverview(filters);
      const fallbackContent = JSON.stringify({ reportType, overview }, null, 2);
      return new Blob([fallbackContent], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }
  },

  /**
   * Generate custom report
   */
  generateCustomReport: async (config: {
    name: string;
    metrics: string[];
    filters?: any;
    groupBy?: string;
  }) => {
    return ApiService.post('/reports/custom', config);
  },

  /**
   * Get report templates
   */
  getReportTemplates: async () => {
    return ApiService.get('/reports/templates');
  },

  /**
   * Schedule recurring report
   */
  scheduleReport: async (config: {
    reportType: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
    filters?: any;
  }) => {
    return ApiService.post('/reports/schedule', config);
  },

  /**
   * Get report history
   */
  getReportHistory: async (limit: number = 10) => {
    return ApiService.get('/reports/history', { params: { limit } });
  },

  /**
   * Delete saved report
   */
  deleteReport: async (reportId: string) => {
    return ApiService.delete(`/reports/${reportId}`);
  },
};

export default ReportsService;
